import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Store, X, Search, Upload, Package } from "lucide-react";
import { api, formatApiErrorDetail, productImageUrl } from "@/lib/api";

export default function AdminBrands({ onOpenProducts }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [logoPath, setLogoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/brands").then(({ data }) => setBrands(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditingId(null); setName(""); setLogoPath(null); setError(""); setShowForm(true); };
  const openEdit = (b) => { setEditingId(b.id); setName(b.name); setLogoPath(b.logo_path || null); setError(""); setShowForm(true); };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setLogoPath(data.path);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError("");
    setSaving(true);
    try {
      const payload = { name: name.trim(), logo_path: logoPath };
      if (editingId) await api.put(`/brands/${editingId}`, payload);
      else await api.post("/brands", payload);
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this brand?")) return;
    await api.delete(`/brands/${id}`);
    load();
  };

  const filtered = brands.filter((b) => b.name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div data-testid="admin-brands">
      {/* Top bar */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="admin-brand-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brand..."
            className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <button
          data-testid="admin-add-brand-button"
          onClick={openNew}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> Add Brand
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-slate-400">No brands found.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filtered.map((b) => (
            <div
              key={b.id}
              data-testid={`admin-brand-${b.id}`}
              className="flex flex-col overflow-hidden rounded-xl border border-blue-100 bg-white shadow-sm transition hover:shadow-md"
            >
              <button
                type="button"
                data-testid={`open-brand-${b.id}`}
                onClick={() => onOpenProducts?.(b.name)}
                className="flex flex-1 flex-col"
              >
              <div className="flex w-full flex-1 items-center justify-center bg-blue-50/50 p-4">
                {b.logo_path ? (
                  <img src={productImageUrl(b.logo_path, "sm")} srcSet={`${productImageUrl(b.logo_path, "sm")} 1x, ${productImageUrl(b.logo_path, "md")} 2x`} alt={b.name} loading="lazy" decoding="async" className="h-20 w-full object-contain" />
                ) : (
                  <div className="flex h-20 w-full items-center justify-center text-blue-200">
                    <Store className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="px-3 py-2 text-center">
                <p className="truncate text-sm font-semibold text-slate-800">{b.name}</p>
              </div>
              </button>
              <div className="border-t border-slate-100">
                <button
                  data-testid={`products-brand-${b.id}`}
                  onClick={() => onOpenProducts?.(b.name)}
                  className="flex w-full items-center justify-center gap-1.5 py-2 text-xs font-semibold text-blue-600 hover:bg-blue-50"
                >
                  <Package className="h-3.5 w-3.5" /> Products
                </button>
                <div className="flex divide-x divide-slate-100 border-t border-slate-100">
                  <button
                    data-testid={`edit-brand-${b.id}`}
                    onClick={() => openEdit(b)}
                    className="flex flex-1 items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button
                    data-testid={`delete-brand-${b.id}`}
                    onClick={() => remove(b.id)}
                    className="flex flex-1 items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowForm(false)} />
          <form onSubmit={save} data-testid="brand-form" className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">{editingId ? "Edit Brand" : "Add Brand"}</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Brand Name</label>
                <input
                  data-testid="admin-brand-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Brand name"
                  className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  autoFocus
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Brand Logo</label>
                <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 p-3">
                  {logoPath ? (
                    <img src={productImageUrl(logoPath, "sm")} alt="" loading="lazy" decoding="async" className="h-20 w-20 rounded object-contain bg-slate-50" />
                  ) : (
                    <div className="grid h-20 w-20 place-items-center rounded bg-slate-50 text-slate-300"><Package className="h-7 w-7" /></div>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="flex w-fit cursor-pointer items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {uploading ? "Uploading..." : logoPath ? "Change logo" : "Upload logo"}
                      <input data-testid="admin-brand-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                    </label>
                    {logoPath && (
                      <button type="button" onClick={() => setLogoPath(null)} className="w-fit text-xs font-medium text-slate-400 hover:text-red-500">Remove logo</button>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Upload your official brand logo (PNG/JPG).</p>
              </div>
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <div className="mt-5 flex gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="save-brand-button" type="submit" disabled={saving} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Brand"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
